import type { ReactNode } from 'react';
import { ArrowRightIcon, CheckIcon, type Icon } from '@phosphor-icons/react';
import { T } from 'gt-react';
import type { FeatureInterestKey } from '@erp/shared-types';

import { Badge } from '@/components/ui/badge';
import { NotifyMeCta } from '@/components/feature-preview/NotifyMeCta';

export interface FeaturePreviewBenefit {
  icon: Icon;
  title: ReactNode;
  body: ReactNode;
}

export interface FeaturePreviewUseCase {
  /** The job in the user's words — short enough to scan. */
  title: ReactNode;
  body: ReactNode;
}

export interface FeaturePreviewStep {
  title: ReactNode;
  body: ReactNode;
}

export interface FeaturePreviewPageProps {
  featureKey: FeatureInterestKey;
  heroIcon: Icon;
  eyebrow: ReactNode;
  headline: ReactNode;
  subhead: ReactNode;
  /** Three or four short outcome chips shown under the hero copy. */
  heroProof: ReactNode[];
  ctaLabel: ReactNode;
  ctaHelper: ReactNode;
  notePrompt: ReactNode;
  notePlaceholder: string;
  painHeading: ReactNode;
  pains: ReactNode[];
  benefitsHeading: ReactNode;
  benefitsSubhead: ReactNode;
  benefits: FeaturePreviewBenefit[];
  useCasesHeading: ReactNode;
  useCasesSubhead: ReactNode;
  useCases: FeaturePreviewUseCase[];
  stepsHeading: ReactNode;
  steps: FeaturePreviewStep[];
  closingHeadline: ReactNode;
  closingBody: ReactNode;
  /** Optional footnote, e.g. "this already exists — your admin can turn it on". */
  accessNote?: ReactNode;
}

/**
 * Shared layout for the pitch pages behind Agents / Workflows / Tools. Unlike
 * the rest of the app this page is deliberately airy — it is a landing page, so
 * it trades information density for a single, obvious next action.
 */
export function FeaturePreviewPage({
  featureKey,
  heroIcon: HeroIcon,
  eyebrow,
  headline,
  subhead,
  heroProof,
  ctaLabel,
  ctaHelper,
  notePrompt,
  notePlaceholder,
  painHeading,
  pains,
  benefitsHeading,
  benefitsSubhead,
  benefits,
  useCasesHeading,
  useCasesSubhead,
  useCases,
  stepsHeading,
  steps,
  closingHeadline,
  closingBody,
  accessNote,
}: FeaturePreviewPageProps) {
  return (
    <div className="mx-auto w-full max-w-6xl px-4 pb-16">
      <section className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-primary/10 via-background to-background p-8 md:p-12">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 -top-24 size-72 rounded-full bg-primary/15 blur-3xl"
        />
        <div className="relative flex flex-col items-start gap-5">
          <Badge
            variant="outline"
            className="gap-1.5 border-primary/30 bg-primary/10 py-1 text-primary"
          >
            <HeroIcon className="size-3.5" weight="fill" />
            {eyebrow}
          </Badge>

          <h1 className="max-w-3xl text-3xl font-bold leading-tight tracking-tight text-balance md:text-5xl">
            {headline}
          </h1>

          <p className="max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            {subhead}
          </p>

          <ul className="flex flex-wrap gap-x-5 gap-y-2">
            {heroProof.map((proof, index) => (
              <li
                // Copy is static per page, so index keys are stable here.
                key={index}
                className="flex items-center gap-1.5 text-sm font-medium"
              >
                <CheckIcon
                  className="size-4 shrink-0 text-emerald-600"
                  weight="bold"
                />
                {proof}
              </li>
            ))}
          </ul>

          <NotifyMeCta
            className="mt-2"
            featureKey={featureKey}
            label={ctaLabel}
            helper={ctaHelper}
            notePrompt={notePrompt}
            notePlaceholder={notePlaceholder}
          />
        </div>
      </section>

      <section className="mt-12 grid gap-6 rounded-2xl border border-dashed bg-muted/30 p-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] md:p-8">
        <h2 className="text-xl font-semibold tracking-tight md:text-2xl">
          {painHeading}
        </h2>
        <ul className="flex flex-col gap-3">
          {pains.map((pain, index) => (
            <li key={index} className="flex items-start gap-3 text-[15px]">
              <span
                aria-hidden="true"
                className="mt-2 size-1.5 shrink-0 rounded-full bg-destructive/70"
              />
              <span className="text-muted-foreground">{pain}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-bold tracking-tight md:text-3xl">
          {benefitsHeading}
        </h2>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          {benefitsSubhead}
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="rounded-xl border bg-card p-5 transition-shadow hover:shadow-sm"
            >
              <div className="flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <benefit.icon className="size-5" weight="fill" />
              </div>
              <h3 className="mt-3 font-semibold">{benefit.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {benefit.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-bold tracking-tight md:text-3xl">
          {useCasesHeading}
        </h2>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          {useCasesSubhead}
        </p>
        <div className="mt-6 divide-y rounded-xl border bg-card">
          {useCases.map((useCase, index) => (
            <div
              key={index}
              className="flex items-start gap-4 p-5 first:rounded-t-xl last:rounded-b-xl"
            >
              <ArrowRightIcon
                className="mt-1 size-4 shrink-0 text-primary"
                weight="bold"
              />
              <div>
                <p className="font-medium">{useCase.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  {useCase.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <h2 className="text-2xl font-bold tracking-tight md:text-3xl">
          {stepsHeading}
        </h2>
        <ol className="mt-6 grid gap-4 md:grid-cols-3">
          {steps.map((step, index) => (
            <li key={index} className="rounded-xl border bg-card p-5">
              <span className="flex size-7 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                {index + 1}
              </span>
              <h3 className="mt-3 font-semibold">{step.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {step.body}
              </p>
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-14 overflow-hidden rounded-2xl border bg-gradient-to-br from-primary/10 via-background to-background p-8 md:p-10">
        <h2 className="max-w-2xl text-2xl font-bold tracking-tight text-balance md:text-3xl">
          {closingHeadline}
        </h2>
        <p className="mt-2 max-w-2xl text-muted-foreground">{closingBody}</p>
        <NotifyMeCta
          className="mt-6"
          featureKey={featureKey}
          label={ctaLabel}
          helper={ctaHelper}
          notePrompt={notePrompt}
          notePlaceholder={notePlaceholder}
        />
        {accessNote ? (
          <p className="mt-6 border-t pt-4 text-sm text-muted-foreground">
            {accessNote}
          </p>
        ) : null}
      </section>

      <p className="mt-8 text-center text-xs text-muted-foreground">
        <T context="footnote on a feature preview page explaining that interest is shared with the product team">
          Requesting access shares your name and organization with our product
          team so we know who to set up first.
        </T>
      </p>
    </div>
  );
}
